import { inject, injectable } from 'inversify'
import { Subscription } from 'rxjs'

import { InjectionTokens } from '@/controller/tokens'

import { UserGeometryChanged } from '@/domain/events/UserGeometryChanged'
import { UserGeometrySynced } from '@/domain/events/UserGeometrySynced'
import { UserGeometry, UserGeometryPojo } from '@/domain/models/UserGeometry/UserGeometry'
import { Result } from '@/domain/protocols/Result'
import { AuthState } from '@/domain/states/AuthState'
import { DeleteUserGeometriesLocalDataUseCase } from '@/domain/useCases/UserGeometries'

import type { IUserGeometryRepository, UserGeometryFiles } from '@/data/UserGeometryRepository/IUserGeometryRepository'

import { IService } from '../IService'

import type {
  IUserGeometriesWebClient,
  UserGeometrySyncRequest
} from '@/infra/http/UserGeometriesWebClient/IUserGeometriesWebClient'
import { UserGeometryFileKind } from '@/infra/localDB/IDexieDB'
import type { ILogger } from '@/infra/logger/ILogger'

export const INITIAL_SYNC_DELAY_MS = 8000
export const CHANGED_DEBOUNCE_MS = 5000

@injectable()
export class UserGeometrySyncService implements IService {
  private inflight: Promise<Result<void>> | null = null
  private cursor: string | null = null
  private authSubscription: Subscription | null = null
  private changedSubscription: Subscription | null = null
  private initialSyncTimeout: ReturnType<typeof setTimeout> | null = null
  private debounceTimeout: ReturnType<typeof setTimeout> | null = null
  private lastSeenToken: string | null = null

  constructor(
    @inject(InjectionTokens.UserGeometryRepository) private repository: IUserGeometryRepository,
    @inject(InjectionTokens.UserGeometriesWebClient) private webClient: IUserGeometriesWebClient,
    @inject(InjectionTokens.AuthState) private authState: AuthState,
    @inject(InjectionTokens.Logger) private logger: ILogger,
    @inject(InjectionTokens.DeleteUserGeometriesLocalDataUseCase)
    private deleteLocalDataUseCase: DeleteUserGeometriesLocalDataUseCase
  ) {}

  async init(): Promise<Result<void>> {
    await this.repository.init()
    this.initialSyncTimeout = setTimeout(() => {
      this.initialSyncTimeout = null
      void this.sync()
    }, INITIAL_SYNC_DELAY_MS)
    return Result.ok()
  }

  start(): Result<void> {
    this.lastSeenToken = this.authState.getStateSnapshot().token
    this.authSubscription = this.authState.getState().subscribe({
      next: (snapshot) => {
        const previous = this.lastSeenToken
        const current = snapshot.token
        this.lastSeenToken = current
        if (previous && !current) {
          void this._onLogout()
        }
      }
    })
    // A local mutation debounces a push by CHANGED_DEBOUNCE_MS so bursts of edits
    // (e.g. styling multiple layers) coalesce into a single sync pass.
    this.changedSubscription = UserGeometryChanged.subscribe(() => {
      this._scheduleDebouncedSync()
    })
    return Result.ok()
  }

  stop(): Result<void> {
    if (this.initialSyncTimeout) {
      clearTimeout(this.initialSyncTimeout)
      this.initialSyncTimeout = null
    }
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout)
      this.debounceTimeout = null
    }
    this.authSubscription?.unsubscribe()
    this.authSubscription = null
    this.changedSubscription?.unsubscribe()
    this.changedSubscription = null
    return Result.ok()
  }

  async sync(): Promise<Result<void>> {
    if (this.inflight) return this.inflight
    this.inflight = this._runSync()
    try {
      return await this.inflight
    } finally {
      this.inflight = null
    }
  }

  private _scheduleDebouncedSync(): void {
    if (this.debounceTimeout) clearTimeout(this.debounceTimeout)
    this.debounceTimeout = setTimeout(() => {
      this.debounceTimeout = null
      void this._syncIfPending()
    }, CHANGED_DEBOUNCE_MS)
  }

  // Only push when there is actually local work, which also breaks the feedback
  // loop from the repository emitting UserGeometryChanged during reconciliation.
  private async _syncIfPending(): Promise<void> {
    const pending = await this.repository.getPendingChanges()
    if (pending.isFailure) return
    const { upserts, deletes } = pending.getValue()
    if (upserts.length === 0 && deletes.length === 0) return
    void this.sync()
  }

  private async _runSync(): Promise<Result<void>> {
    if (!this.authState.getStateSnapshot().token) {
      return Result.fail('NOT_AUTHENTICATED')
    }

    const pendingResult = await this.repository.getPendingChanges()
    if (pendingResult.isFailure) {
      return Result.fail(pendingResult.error)
    }
    const { upserts, deletes } = pendingResult.getValue()

    const request: UserGeometrySyncRequest = {
      since: this.cursor,
      upserts: upserts.map((geometry) => geometry.toPojo()),
      deletes: deletes.map((geometry) => ({
        id: geometry.id.toString(),
        updatedAt: geometry.updatedAt ?? new Date().toISOString()
      }))
    }

    const response = await this.webClient.sync(request)
    if (response.isFailure) {
      this.logger.error('UserGeometrySyncService', `sync failed: ${response.error}`)
      return Result.fail(response.error)
    }

    const payload = response.getValue()

    // Pull server-side changes first, then push our binaries and drop tombstones.
    const upsertsReconciled = await this._processServerUpserts(payload.upserts)
    await this._processServerDeletes(payload.deletes.map((entry) => entry.id))

    for (const geometry of upserts) {
      await this._syncUpsertFiles(geometry)
    }

    await this.repository.hardDelete(deletes.map((geometry) => geometry.id.toString()))

    // Re-download `.fgb` blobs the server has but that are missing locally
    // (e.g. geometries created on another device, or an evicted blob).
    await this._selfHealMissingFiles()

    // Hold the cursor back if any server upsert could not be fully persisted, so
    // the next pass re-pulls it instead of skipping past it.
    if (upsertsReconciled) {
      this.cursor = payload.serverNow
    }

    UserGeometrySynced.next()
    return Result.ok()
  }

  private async _processServerUpserts(upserts: UserGeometryPojo[]): Promise<boolean> {
    let allReconciled = true
    for (const pojo of upserts) {
      const reconciled = await this._persistServerGeometry(pojo)
      if (!reconciled) allReconciled = false
    }
    return allReconciled
  }

  private async _persistServerGeometry(pojo: UserGeometryPojo): Promise<boolean> {
    const geometry = UserGeometry.fromPojo(pojo)
    if (!geometry) return true

    const id = geometry.id.toString()
    const existing = await this.repository.getGeometry(geometry.id)
    if (existing.isSuccess) {
      const local = existing.getValue()
      // Local edits win until they are pushed (this also no-ops our own echoes);
      // an already in-sync row needs nothing.
      if (!local.synced || local.updatedAt === geometry.updatedAt) {
        return true
      }
    }

    // The repository persists metadata and both blobs together, so a pulled
    // geometry needs both binaries before it can be stored.
    const processed = await this.webClient.downloadProcessed(id)
    if (processed.isFailure) {
      this.logger.error('UserGeometrySyncService', `download .fgb failed for ${id}: ${processed.error}`)
      return false
    }

    const original = await this.webClient.downloadOriginal(id)
    if (original.isFailure) {
      this.logger.error('UserGeometrySyncService', `download original failed for ${id}: ${original.error}`)
      return false
    }

    const downloadedOriginal = original.getValue()
    const files: UserGeometryFiles = {
      processed: processed.getValue(),
      original: { blob: downloadedOriginal.blob, ext: downloadedOriginal.ext }
    }

    const saved = await this.repository.saveGeometry(geometry, files)
    if (saved.isFailure) {
      this.logger.error('UserGeometrySyncService', `persist server geometry ${id} failed: ${saved.error}`)
      return false
    }

    return true
  }

  private async _processServerDeletes(ids: string[]): Promise<void> {
    if (ids.length === 0) return
    await this.repository.hardDelete(ids)
  }

  private async _syncUpsertFiles(geometry: UserGeometry): Promise<void> {
    const id = geometry.id.toString()
    const needsProcessed = !geometry.processedSynced
    const needsOriginal = !geometry.originalSynced

    // Metadata-only change (e.g. rename): the push already synced the metadata.
    // The optimistic guard re-pushes if the row was edited after this push began.
    if (!needsProcessed && !needsOriginal) {
      await this.repository.markSynced(id, geometry.updatedAt)
      return
    }

    if (needsProcessed) {
      await this._uploadFile(geometry, 'processed')
    }
    if (needsOriginal) {
      await this._uploadFile(geometry, 'original')
    }
  }

  // Uploads one binary; markFileSynced auto-marks the geometry synced once both
  // binaries are uploaded, so a partial upload leaves it pending for a retry.
  private async _uploadFile(geometry: UserGeometry, kind: UserGeometryFileKind): Promise<void> {
    const id = geometry.id.toString()

    const fileResult = await this.repository.getFile(geometry.id, kind)
    if (fileResult.isFailure) {
      this.logger.error('UserGeometrySyncService', `read ${kind} file failed for ${id}: ${fileResult.error}`)
      return
    }

    const file = fileResult.getValue()
    if (!file) return

    const upload =
      kind === 'processed'
        ? await this.webClient.uploadProcessed(id, file.blob)
        : await this.webClient.uploadOriginal(id, file.blob, file.ext)

    if (upload.isFailure) {
      this.logger.error('UserGeometrySyncService', `upload ${kind} failed for ${id}: ${upload.error}`)
      return
    }

    await this.repository.markFileSynced(id, kind)
  }

  private async _selfHealMissingFiles(): Promise<void> {
    const geometriesResult = await this.repository.getGeometries()
    if (geometriesResult.isFailure) return

    for (const geometry of geometriesResult.getValue()) {
      const hasProcessed = await this.repository.hasFile(geometry.id, 'processed')
      if (hasProcessed.isFailure || hasProcessed.getValue()) continue

      const id = geometry.id.toString()
      const download = await this.webClient.downloadProcessed(id)
      if (download.isFailure) {
        this.logger.warn('UserGeometrySyncService', `self-heal .fgb failed for ${id}: ${download.error}`)
        continue
      }

      const put = await this.repository.putFile(geometry.id, 'processed', { blob: download.getValue() })
      if (put.isFailure) {
        this.logger.warn('UserGeometrySyncService', `self-heal write failed for ${id}: ${put.error}`)
      }
    }
  }

  private async _onLogout(): Promise<void> {
    this.cursor = null
    const result = await this.deleteLocalDataUseCase.execute()
    if (result.isFailure) {
      this.logger.error('UserGeometrySyncService', `logout cleanup failed: ${result.error}`)
    }
  }
}
